import type { SafetyFieldConfig, SafetySectionDefinition } from './types';
import { unitFieldTooltips } from '@/features/companies/components/masterDataFieldTooltips';
import { generateSafetyFormSections } from '../../utils/safetyDisplayHelpers';

/**
 * Section definitions for Ventilazione (Ventilation) sub-tab.
 */
export const ventilazioneSections: Record<string, SafetySectionDefinition> = {
  main: { title: 'Ventilazione principale', order: 1 },
  natural: { title: 'Ventilazione naturale', order: 2 },
  extractors: { title: 'Estrattori meccanici', order: 3 },
} as const;

/**
 * Field definitions for Ventilazione (Ventilation) sub-tab.
 */
export const ventilazioneFields: readonly SafetyFieldConfig[] = [
  // Main ventilation
  {
    key: 'ventilationType',
    dataPath: 'technical.safety.ventilation.type',
    label: 'Tipo',
    type: 'text',
    placeholder: 'Es: Ventilazione meccanica',
    tooltip: unitFieldTooltips.ventilation.ventilationType,
    panel: 'main',
    section: 'main',
  },
  {
    key: 'ventilationFlowRateM3h',
    dataPath: 'technical.safety.ventilation.flowRateM3h',
    label: 'Portata estrattori',
    type: 'number',
    placeholder: 'Es: 10000',
    tooltip: unitFieldTooltips.ventilation.ventilationFlowRateM3h,
    panel: 'main',
    section: 'main',
    unit: 'm³/h',
  },
  {
    key: 'ventilationNotes',
    dataPath: 'technical.safety.ventilation.notes',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Note facoltative',
    tooltip: unitFieldTooltips.ventilation.ventilationNotes,
    panel: 'main',
    section: 'main',
    colSpan: 2,
  },
  // Natural ventilation
  {
    key: 'ventilationNaturalHighM2',
    dataPath: 'technical.safety.ventilation.natural.highOpeningsAreaM2',
    label: 'Aperture alte',
    type: 'number',
    placeholder: 'Es: 2.5',
    tooltip: unitFieldTooltips.ventilation.ventilationNaturalHighM2,
    panel: 'natural',
    section: 'natural',
    unit: 'm²',
  },
  {
    key: 'ventilationNaturalLowM2',
    dataPath: 'technical.safety.ventilation.natural.lowOpeningsAreaM2',
    label: 'Aperture basse',
    type: 'number',
    placeholder: 'Es: 1.2',
    tooltip: unitFieldTooltips.ventilation.ventilationNaturalLowM2,
    panel: 'natural',
    section: 'natural',
    unit: 'm²',
  },
  {
    key: 'ventilationNaturalNotes',
    dataPath: 'technical.safety.ventilation.natural.notes',
    label: 'Note',
    type: 'text',
    placeholder: 'Note facoltative',
    tooltip: unitFieldTooltips.ventilation.ventilationNaturalNotes,
    panel: 'natural',
    section: 'natural',
    colSpan: 2,
  },
  // Extractors
  {
    key: 'ventilationExtractUnits',
    dataPath: 'technical.safety.ventilation.extractors.units',
    label: 'Unità',
    type: 'number',
    placeholder: 'Es: 2',
    tooltip: unitFieldTooltips.ventilation.ventilationExtractUnits,
    panel: 'extractors',
    section: 'extractors',
  },
  {
    key: 'ventilationExtractFlowM3h',
    dataPath: 'technical.safety.ventilation.extractors.airflowM3h',
    label: 'Portata aria',
    type: 'number',
    placeholder: 'Es: 5000',
    tooltip: unitFieldTooltips.ventilation.ventilationExtractFlowM3h,
    panel: 'extractors',
    section: 'extractors',
    unit: 'm³/h',
  },
  {
    key: 'ventilationExtractLogic',
    dataPath: 'technical.safety.ventilation.extractors.logic',
    label: 'Logica',
    type: 'text',
    placeholder: 'Es: Attivazione su allarme',
    tooltip: unitFieldTooltips.ventilation.ventilationExtractLogic,
    panel: 'extractors',
    section: 'extractors',
  },
  {
    key: 'ventilationExtractMode',
    dataPath: 'technical.safety.ventilation.extractors.mode',
    label: 'Modalità',
    type: 'text',
    placeholder: 'Es: Automatico',
    tooltip: unitFieldTooltips.ventilation.ventilationExtractMode,
    panel: 'extractors',
    section: 'extractors',
  },
  {
    key: 'ventilationExtractAtex',
    dataPath: 'technical.safety.ventilation.extractors.atex',
    label: 'Certificazione ATEX',
    type: 'checkbox',
    tooltip: unitFieldTooltips.ventilation.ventilationExtractAtex,
    panel: 'extractors',
    section: 'extractors',
    displayFormatter: 'boolean',
  },
] as const;

/**
 * Form sections derived from field definitions.
 */
export const ventilazioneFormSections = generateSafetyFormSections(
  ventilazioneFields,
  ['main', 'natural', 'extractors'],
  {
    main: 'Ventilazione principale',
    natural: 'Ventilazione naturale',
    extractors: 'Estrattori meccanici',
  }
);
